/**
 * Conta — tabbed identity + assinatura + payment + charges + delete.
 * Sign-out lives only in the shell. Source: docs/meta/prd-my-x9-portal-ia-2026-08.md.
 */
import { useMemo, useState } from 'react';
import {
  IoChevronDown,
  IoChevronUp,
  IoDownloadOutline,
} from 'react-icons/io5';
import {
  AMOUNTS_CENTAVOS,
  BASE_TEAM_SEATS,
  BASE_TEAM_PRICE_CENTAVOS,
  EXTRA_SEAT_PRICE_CENTAVOS,
  MAX_EXTRA_SEATS,
  MAX_VOLUME_PACKS,
  PORTAL_PATHS,
  SEAT_PACK_INCLUDED_INBOUND,
  INBOUND_FREE_MONTHLY,
  FREE_INCLUDED_SEATS,
  INBOUND_PACK_VOLUME,
  INBOUND_PACK_PRICE_CENTAVOS,
  monthsUntilYmd,
  packPricing,
  resolveSeatCount,
  yearlyMidCycleUpgradeCharge,
  type PackInterval,
} from '@x9/billing';
import PageTabs from '../components/PageTabs';
import PortalShell from '../components/PortalShell';
import PortalLoader from '../components/PortalLoader';
import PlanPicker from '../components/PlanPicker';
import UsageDashboard, { type UsageCheckout } from '../components/UsageDashboard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { getFirebaseAuth } from '../config/firebase';
import { useBillingSummary } from '../hooks/useBillingSummary';
import { cn } from '../lib/utils';
import {
  cancelMySubscription,
  updateSubscriptionPacks,
  deleteUserAccount,
  callableErrorMessage,
  shouldFallbackToCheckout,
  getPaymentDocumentUrls,
  requestPaymentRefund,
  type BillingPaymentSummary,
} from '../services/api';
import { signOutBilling } from '../services/phoneAuth';

type AccountTab = 'uso' | 'assinatura' | 'pagamento' | 'cobrancas' | 'detalhes';

type Props = {
  onNavigate: (path: string) => void;
  onSelectMonthly: () => void;
  onSelectYearly: () => void;
  onCheckout: UsageCheckout;
  verifiedEmail: string | null;
  emailVerified: boolean;
  onAddEmail: () => void;
  onSignedOut: () => void;
  /** `/usage` and `?tab=uso` open the ledger tab. */
  initialTab?: AccountTab;
};

const TABS: Array<{ id: AccountTab; label: string }> = [
  { id: 'uso', label: 'Uso' },
  { id: 'assinatura', label: 'Assinatura' },
  { id: 'pagamento', label: 'Pagamento' },
  { id: 'cobrancas', label: 'Cobranças' },
  { id: 'detalhes', label: 'Detalhes' },
];

function parseAccountTab(search = window.location.search): AccountTab {
  const raw = new URLSearchParams(search).get('tab');
  if (raw === 'assinatura' || raw === 'plan' || raw === 'billing') return 'assinatura';
  if (raw === 'pagamento' || raw === 'payment') return 'pagamento';
  if (raw === 'cobrancas' || raw === 'history') return 'cobrancas';
  if (raw === 'detalhes') return 'detalhes';
  if (raw === 'uso' || raw === 'usage') return 'uso';
  return 'uso';
}

function writeAccountTab(tab: AccountTab) {
  const url = new URL(window.location.href);
  if (url.pathname.replace(/\/+$/, '') === PORTAL_PATHS.USAGE) return;
  url.searchParams.set('tab', tab);
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function brl(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatPeriod(start: string | null | undefined, end: string | null | undefined): string {
  if (!start && !end) return '—';
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  if (start && end) return `${fmt(start)} → ${fmt(end)}`;
  return start ? fmt(start) : fmt(end!);
}

function discountLabel(p: BillingPaymentSummary): string {
  const d = p.discount;
  if (!d) return '—';
  const parts: string[] = [];
  if (d.yearlyPercent && (d.yearlySavingsCents ?? 0) > 0) {
    parts.push(`Anual −${d.yearlyPercent}% (${brl(d.yearlySavingsCents)})`);
  } else if (d.yearlyPercent) {
    parts.push(`Anual −${d.yearlyPercent}%`);
  }
  if (d.upgradeCreditDays && d.upgradeCreditDays > 0) {
    parts.push(`Crédito ${d.upgradeCreditDays} dia${d.upgradeCreditDays === 1 ? '' : 's'} (mensal restante)`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function ptDateLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
}

/** Infer seat/volume before→after for mid-cycle rows that predate `change`. */
function inferMidCycleChange(p: BillingPaymentSummary): BillingPaymentSummary['change'] | null {
  if (p.change) return p.change;
  const seats = p.seatCount;
  const vol = p.volumePacks ?? 0;
  const deltaMonthly = p.discount?.deltaMonthlyCents;
  const months = p.discount?.openMonths ?? null;
  if (seats == null) return null;

  let previousSeatCount: number | null = null;
  let previousVolumePacks: number | null = null;
  let deltaSeats: number | null = null;
  let deltaVolumePacks: number | null = null;

  if (Number.isFinite(Number(deltaMonthly)) && Number(deltaMonthly) > 0) {
    const dm = Number(deltaMonthly);
    if (dm % EXTRA_SEAT_PRICE_CENTAVOS === 0) {
      deltaSeats = dm / EXTRA_SEAT_PRICE_CENTAVOS;
      previousSeatCount = seats - deltaSeats;
      previousVolumePacks = vol;
      deltaVolumePacks = 0;
    } else if (dm % INBOUND_PACK_PRICE_CENTAVOS === 0) {
      deltaVolumePacks = dm / INBOUND_PACK_PRICE_CENTAVOS;
      previousVolumePacks = vol - deltaVolumePacks;
      previousSeatCount = seats;
      deltaSeats = 0;
    } else {
      // Mixed seat + volume deltas (small brute force).
      outer: for (let ds = 0; ds <= Math.min(20, seats); ds += 1) {
        for (let dv = 0; dv <= Math.min(20, vol); dv += 1) {
          if (ds === 0 && dv === 0) continue;
          if (ds * EXTRA_SEAT_PRICE_CENTAVOS + dv * INBOUND_PACK_PRICE_CENTAVOS === dm) {
            deltaSeats = ds;
            deltaVolumePacks = dv;
            previousSeatCount = seats - ds;
            previousVolumePacks = vol - dv;
            break outer;
          }
        }
      }
    }
  }

  const nextRenewalChargeCents = packPricing({
    seatCount: seats,
    volumePacks: vol,
    interval: 'YEARLY',
  }).chargeCents;

  return {
    previousSeatCount,
    seatCount: seats,
    previousVolumePacks,
    volumePacks: vol,
    deltaSeats,
    deltaVolumePacks,
    openMonths: months,
    renewalDueDate: p.billingPeriodEnd,
    nextRenewalChargeCents,
  };
}

function whatLabel(p: BillingPaymentSummary): string {
  if (p.kind === 'YEARLY_MIDCYCLE_UPGRADE') {
    const ch = inferMidCycleChange(p);
    if (ch) {
      const parts: string[] = [];
      if (ch.deltaSeats != null && ch.deltaSeats !== 0
        && ch.previousSeatCount != null && ch.seatCount != null) {
        const signed = ch.deltaSeats > 0 ? `+${ch.deltaSeats}` : String(ch.deltaSeats);
        parts.push(
          `${signed} assento${Math.abs(ch.deltaSeats) === 1 ? '' : 's'} (${ch.previousSeatCount} → ${ch.seatCount})`,
        );
      }
      if (ch.deltaVolumePacks != null && ch.deltaVolumePacks !== 0
        && ch.previousVolumePacks != null && ch.volumePacks != null) {
        const signed = ch.deltaVolumePacks > 0 ? `+${ch.deltaVolumePacks}` : String(ch.deltaVolumePacks);
        parts.push(`${signed} volume (${ch.previousVolumePacks} → ${ch.volumePacks})`);
      }
      const months = ch.openMonths ?? null;
      const monthWord = months === 1 ? 'mês' : 'meses';
      const renewalDate = ptDateLabel(ch.renewalDueDate) || ptDateLabel(p.billingPeriodEnd);
      const until = months != null
        ? (renewalDate
          ? `por ${months} ${monthWord} até ${renewalDate}`
          : `por ${months} ${monthWord} até a renovação`)
        : null;
      const head = parts.length
        ? `Ampliação Equipe: ${parts.join(' e ')}`
        : (ch.seatCount != null
          ? `Ampliação Equipe (${ch.seatCount} assentos${ch.volumePacks ? ` + ${ch.volumePacks} volume` : ''})`
          : 'Ampliação Equipe');
      const now = brl(p.chargeCents ?? p.grossAmountInCents);
      const renewal = ch.nextRenewalChargeCents != null ? brl(ch.nextRenewalChargeCents) : null;
      return [
        head,
        until ? `Cobrança proporcional ${until} (${now} agora)` : null,
        renewal ? `Próxima renovação anual: ${renewal}` : null,
      ].filter(Boolean).join('. ') + '.';
    }
  }
  if (p.description) return p.description;
  return 'Cobrança';
}

function faturaUrl(p: BillingPaymentSummary): string | null {
  return p.asaasInvoiceUrl || p.asaasTransactionReceiptUrl || null;
}

/** First clause for collapsed mobile rows (keeps the list scannable). */
function whatSummary(p: BillingPaymentSummary): string {
  const full = whatLabel(p);
  const cut = full.split('. ')[0] || full;
  return cut.length > 96 ? `${cut.slice(0, 93)}…` : cut;
}

function paymentDateLabel(p: BillingPaymentSummary, withTime = false): string {
  const raw = p.confirmedAt || p.createdAt;
  if (!raw) return '—';
  const d = new Date(raw);
  return withTime ? d.toLocaleString('pt-BR') : d.toLocaleDateString('pt-BR');
}

/** Customer-facing charge status for Cobranças. */
function paymentChargeStatus(p: BillingPaymentSummary): {
  key: 'paid' | 'canceled' | 'pending' | 'other';
  label: string;
} {
  const refund = String(p.refundStatus || '').toUpperCase();
  const status = String(p.status || '').toUpperCase();
  if (refund === 'REFUNDED' || status === 'REFUNDED') {
    return { key: 'canceled', label: 'Cancelado' };
  }
  if (refund === 'PENDING') {
    return { key: 'pending', label: 'Estorno pendente' };
  }
  if (status === 'CONFIRMED' || status === 'RECEIVED') {
    return { key: 'paid', label: 'Pago' };
  }
  if (status) {
    return { key: 'other', label: status };
  }
  return { key: 'paid', label: 'Pago' };
}

function paymentStatusDotClass(key: ReturnType<typeof paymentChargeStatus>['key']): string {
  if (key === 'canceled') return 'bg-red-500';
  if (key === 'pending') return 'bg-amber-500';
  if (key === 'paid') return 'bg-emerald-500';
  return 'bg-muted-foreground';
}

function ChargeStatusBadge({
  status,
}: {
  status: ReturnType<typeof paymentChargeStatus>;
}) {
  return (
    <Badge variant="outline" className="whitespace-nowrap">
      <span
        className={cn('h-1.5 w-1.5 shrink-0 rounded-full', paymentStatusDotClass(status.key))}
        aria-hidden
      />
      {status.label}
    </Badge>
  );
}

type DocModalState = { payment: BillingPaymentSummary };

type PlanKey = 'FREE' | 'MONTHLY' | 'YEARLY';

type ActiveSub = {
  status?: string;
  billingInterval?: string;
  seatCount?: number;
  seatPacks?: number;
  volumePacks?: number;
  pendingSeatCount?: number | null;
  pendingSeatPacks?: number | null;
  pendingVolumePacks?: number | null;
  cancelAtPeriodEnd?: boolean;
  renewalDueDate?: string | null;
  nextDueDate?: string | null;
  asaasValueSync?: string | null;
};

function activeSubscription(
  summary: ReturnType<typeof useBillingSummary>['summary'],
): ActiveSub | null {
  const activeStatuses = ['ACTIVE', 'CANCEL_AT_PERIOD_END', 'PAST_DUE', 'GRACE_PERIOD'];
  const activeSubs = ((summary?.subscriptions || []) as ActiveSub[]).filter((s) =>
    activeStatuses.includes(String(s.status || '')),
  );
  return (
    activeSubs.find((s) => String(s.billingInterval || '').toUpperCase() === 'YEARLY') ||
    activeSubs[0] ||
    null
  );
}

function inboundMonthly(seats: number, volumePacks: number): number {
  return INBOUND_FREE_MONTHLY
    + (seats > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
    + volumePacks * INBOUND_PACK_VOLUME;
}

/** Next invoice the customer is contracted for (pending decreases apply here). */
function nextScheduledCharge(
  sub: ActiveSub | null,
  hasPaidAccess: boolean,
): {
  dueLabel: string | null;
  chargeCents: number;
  interval: PackInterval;
  nextSeats: number;
  nextVolume: number;
  currentSeats: number;
  currentVolume: number;
  cancelling: boolean;
  structureChanged: boolean;
} | null {
  if (!sub || !hasPaidAccess) return null;
  const cancelling = !!(sub.cancelAtPeriodEnd || String(sub.status || '') === 'CANCEL_AT_PERIOD_END');
  const interval: PackInterval =
    String(sub.billingInterval || 'MONTHLY').toUpperCase() === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const currentSeats = resolveSeatCount(sub);
  const currentVolume = Number.isFinite(Number(sub.volumePacks)) ? Number(sub.volumePacks) : 0;
  const nextSeats = Number.isFinite(Number(sub.pendingSeatCount)) && sub.pendingSeatCount != null
    ? Number(sub.pendingSeatCount)
    : currentSeats;
  const nextVolume = Number.isFinite(Number(sub.pendingVolumePacks)) && sub.pendingVolumePacks != null
    ? Number(sub.pendingVolumePacks)
    : currentVolume;
  const dueRaw = sub.renewalDueDate || sub.nextDueDate || null;
  const dueLabel = ptDateLabel(dueRaw);
  let chargeCents = 0;
  try {
    chargeCents = packPricing({
      seatCount: nextSeats,
      volumePacks: nextVolume,
      interval,
    }).chargeCents;
  } catch {
    return null;
  }
  return {
    dueLabel,
    chargeCents,
    interval,
    nextSeats,
    nextVolume,
    currentSeats,
    currentVolume,
    cancelling,
    structureChanged: nextSeats !== currentSeats || nextVolume !== currentVolume,
  };
}

function currentPlan(
  summary: ReturnType<typeof useBillingSummary>['summary'],
  sub: ActiveSub | null,
): { key: PlanKey; title: string; detail: string } {
  if (!summary?.hasPaidAccess) {
    return {
      key: 'FREE',
      title: 'Plano gratuito',
      detail: `Uso pessoal ilimitado · ${INBOUND_FREE_MONTHLY} contribuições/mês de outras pessoas · ${FREE_INCLUDED_SEATS} assento de colega`,
    };
  }
  const seatCount = resolveSeatCount(sub);
  const volumePacks = Number.isFinite(Number(sub?.volumePacks)) ? Number(sub?.volumePacks) : 0;
  const interval = String(sub?.billingInterval || 'MONTHLY').toUpperCase();
  let pricing;
  try {
    pricing = packPricing({
      seatCount,
      volumePacks,
      interval: interval === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
    });
  } catch {
    return {
      key: 'MONTHLY',
      title: 'Plano Equipe',
      detail: 'Assinatura ativa — escolha assentos extras abaixo para continuar.',
    };
  }
  const inbound =
    INBOUND_FREE_MONTHLY
    + (seatCount > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
    + volumePacks * INBOUND_PACK_VOLUME;
  const parts = [
    seatCount > 0 ? `${seatCount} assentos` : null,
    `${inbound} contribuições/mês`,
  ].filter(Boolean);
  if (interval === 'YEARLY') {
    return {
      key: 'YEARLY',
      title: 'Plano Equipe · anual',
      detail: `${brl(pricing.chargeCents)}/ano · ${parts.join(' · ')}`,
    };
  }
  return {
    key: 'MONTHLY',
    title: 'Plano Equipe · mensal',
    detail: `${brl(pricing.chargeCents)}/mês · ${parts.join(' · ')}`,
  };
}

function PackStepper({
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          width="auto"
          size="sm"
          aria-label={`Diminuir ${label}`}
          disabled={disabled || value <= min}
          onClick={() => onChange(value - 1)}
        >
          −
        </Button>
        <span className="w-8 text-center text-base font-bold tabular-nums text-foreground">
          {value}
        </span>
        <Button
          variant="secondary"
          width="auto"
          size="sm"
          aria-label={`Aumentar ${label}`}
          disabled={disabled || value >= max}
          onClick={() => onChange(value + 1)}
        >
          +
        </Button>
      </div>
    </div>
  );
}

export default function AccountPage({
  onNavigate,
  onSelectMonthly,
  onSelectYearly,
  onCheckout,
  verifiedEmail,
  emailVerified,
  onAddEmail,
  onSignedOut,
  initialTab,
}: Props) {
  const user = getFirebaseAuth().currentUser;
  const { summary, loading, error, refresh, invalidate } = useBillingSummary(true);
  const sub = activeSubscription(summary);
  const plan = currentPlan(summary, sub);
  const profile = summary?.billingProfile;
  const scheduled = nextScheduledCharge(sub, !!summary?.hasPaidAccess);

  const [tab, setTab] = useState<AccountTab>(() => initialTab ?? parseAccountTab());
  const changeTab = (next: AccountTab) => {
    setTab(next);
    writeAccountTab(next);
  };
  const [extraSeats, setExtraSeats] = useState<number | null>(null);
  const [volumePacks, setVolumePacks] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [packMsg, setPackMsg] = useState<string | null>(null);
  const [packErr, setPackErr] = useState<string | null>(null);
  const [docModal, setDocModal] = useState<DocModalState | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docMsg, setDocMsg] = useState<string | null>(null);
  const [cdcConfirm, setCdcConfirm] = useState(false);
  const [cdcBusy, setCdcBusy] = useState(false);
  const [cdcErr, setCdcErr] = useState<string | null>(null);
  const [cdcOk, setCdcOk] = useState<string | null>(null);
  const [paymentOverrides, setPaymentOverrides] = useState<Record<string, BillingPaymentSummary>>({});
  const [expandedCharges, setExpandedCharges] = useState<Record<string, boolean>>({});
  const [reduceExtra, setReduceExtra] = useState<number | null>(null);
  const [reduceVolume, setReduceVolume] = useState<number | null>(null);
  const [reduceMsg, setReduceMsg] = useState<string | null>(null);
  const [reduceErr, setReduceErr] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelMsg, setCancelMsg] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'final'>('idle');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const payments = useMemo(() => {
    const list = summary?.payments || [];
    return list.map((p) => paymentOverrides[p.id] || p);
  }, [summary?.payments, paymentOverrides]);

  const interval: PackInterval =
    String(sub?.billingInterval || 'MONTHLY').toUpperCase() === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const currentSeatCount = resolveSeatCount(sub);
  const currentExtraSeats = Math.max(0, currentSeatCount - BASE_TEAM_SEATS);
  const currentVolumePacks = Number.isFinite(Number(sub?.volumePacks)) ? Number(sub?.volumePacks) : 0;
  const cancelling = sub?.cancelAtPeriodEnd === true || sub?.status === 'CANCEL_AT_PERIOD_END';

  function openDownloadModal(p: BillingPaymentSummary) {
    setDocMsg(null);
    setCdcConfirm(false);
    setCdcErr(null);
    setCdcOk(null);
    setDocModal({ payment: p });
  }

  function cdcConsequenceCopy(p: BillingPaymentSummary): string {
    if (p.kind === 'YEARLY_MIDCYCLE_UPGRADE') {
      const fromSeats = p.change?.previousSeatCount;
      const fromVol = p.change?.previousVolumePacks;
      if (fromSeats != null) {
        return `O valor desta ampliação será estornado e sua capacidade volta para ${fromSeats} assentos`
          + (fromVol != null ? ` e ${fromVol} pacote(s) de volume` : '')
          + '. A assinatura anual continua ativa.';
      }
      return 'O valor desta ampliação será estornado e a capacidade volta ao estado anterior. A assinatura anual continua ativa.';
    }
    return 'O valor desta compra será estornado e o acesso pago será encerrado imediatamente (plano gratuito).';
  }

  async function confirmCdcRefund() {
    if (!docModal) return;
    setCdcBusy(true);
    setCdcErr(null);
    setCdcOk(null);
    try {
      const res = await requestPaymentRefund(docModal.payment.id);
      if (res.payment) {
        setPaymentOverrides((prev) => ({ ...prev, [res.payment!.id]: res.payment! }));
      }
      setCdcConfirm(false);
      setCdcOk(
        res.alreadyRefunded
          ? 'Esta compra já havia sido estornada. O crédito no cartão pode levar até 10 dias úteis.'
          : 'Compra cancelada. O estorno foi solicitado à operadora — o crédito no cartão pode levar até 10 dias úteis.',
      );
      invalidate();
      await refresh({ force: true });
    } catch (e) {
      setCdcErr(callableErrorMessage(e) || 'Não foi possível cancelar esta compra agora.');
    } finally {
      setCdcBusy(false);
    }
  }

  function toggleChargeExpanded(id: string) {
    setExpandedCharges((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function downloadFaturaFromModal() {
    if (!docModal) return;
    const current = paymentOverrides[docModal.payment.id] || docModal.payment;
    const url = faturaUrl(current);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setDocBusy(true);
    setDocMsg(null);
    try {
      const fresh = await getPaymentDocumentUrls(current.id);
      setPaymentOverrides((prev) => ({ ...prev, [fresh.id]: fresh }));
      setDocModal({ payment: fresh });
      const nextUrl = faturaUrl(fresh);
      if (nextUrl) {
        window.open(nextUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      setDocMsg('A fatura ainda não está disponível nesta cobrança. Tente de novo em instantes.');
    } catch (e) {
      setDocMsg(callableErrorMessage(e) || 'Não foi possível atualizar a fatura agora.');
    } finally {
      setDocBusy(false);
    }
  }

  function downloadNfFromModal() {
    if (!docModal) return;
    const current = paymentOverrides[docModal.payment.id] || docModal.payment;
    if (current.nfsePdfUrl) {
      window.open(current.nfsePdfUrl, '_blank', 'noopener,noreferrer');
      setDocMsg(null);
      return;
    }
    setDocMsg(
      'A nota fiscal (NFS-e) ainda não foi emitida. A emissão fiscal municipal está em configuração'
      + (faturaUrl(current) ? ' — use a Fatura enquanto isso.' : '.'),
    );
  }

  // Buy pipeline — increase only.
  const ex = Math.max(currentExtraSeats, extraSeats ?? currentExtraSeats);
  const sc = currentSeatCount === 0 && currentExtraSeats === 0 && extraSeats === null
    ? 0
    : (currentSeatCount === 0 && extraSeats !== null
      ? BASE_TEAM_SEATS + extraSeats
      : BASE_TEAM_SEATS + ex);
  const vp = Math.max(currentVolumePacks, volumePacks ?? currentVolumePacks);
  const dirty = sc > currentSeatCount || vp > currentVolumePacks;
  const targetValid = sc + vp >= 1;
  const preview = useMemo(
    () => (targetValid ? packPricing({ seatCount: sc, volumePacks: vp, interval }) : null),
    [sc, vp, interval, targetValid],
  );

  // Cancel / retention pipeline — decrease only (pends to renewal).
  const redEx = Math.min(currentExtraSeats, reduceExtra ?? currentExtraSeats);
  const redSc = currentSeatCount === 0
    ? 0
    : BASE_TEAM_SEATS + redEx;
  const redVp = Math.min(currentVolumePacks, reduceVolume ?? currentVolumePacks);
  const reduceDirty = redSc < currentSeatCount || redVp < currentVolumePacks;
  const reduceValid = redSc + redVp >= 1;
  const reducePreview = useMemo(
    () => (reduceValid ? packPricing({ seatCount: redSc, volumePacks: redVp, interval }) : null),
    [redSc, redVp, interval, reduceValid],
  );

  const savePacks = async () => {
    setPackErr(null);
    setPackMsg(null);
    if (!dirty || sc < currentSeatCount || vp < currentVolumePacks) {
      setPackErr('Este fluxo só amplia o plano. Para reduzir, use a seção abaixo.');
      return;
    }
    // No in-force Asaas sub (test leftover entitlement, cancelled, etc.) → first-buy checkout.
    if (!sub) {
      onNavigate(
        `${PORTAL_PATHS.CHECKOUT}?interval=monthly&seatCount=${sc}&volumePacks=${vp}`,
      );
      return;
    }
    // Yearly capacity increase → Pay-now checkout (do not charge from Conta steppers).
    if (midCyclePreview && midCyclePreview.chargeNowCents > 0) {
      onNavigate(
        `${PORTAL_PATHS.CHECKOUT}?mode=pack-upgrade&seatCount=${sc}&volumePacks=${vp}`,
      );
      return;
    }
    setBusy(true);
    try {
      const res = await updateSubscriptionPacks(sc, vp);
      if (res.requiresCheckout) {
        onNavigate(
          `${PORTAL_PATHS.CHECKOUT}?mode=pack-upgrade&seatCount=${sc}&volumePacks=${vp}`,
        );
        return;
      }
      const msgs: string[] = [];
      msgs.push('Aumento aplicado: a capacidade extra já está disponível.');
      if (res.midCycleChargeCents && res.midCycleChargeCents > 0) {
        msgs.push(
          `Cobrado agora: ${brl(res.midCycleChargeCents)} (${res.midCycleOpenMonths} mês(es) até a renovação).`,
        );
      } else if (interval === 'MONTHLY') {
        msgs.push('O valor da próxima fatura mensal foi atualizado.');
      }
      msgs.push(`Próxima cobrança: ${brl(res.nextChargeCents)}${interval === 'YEARLY' ? '/ano' : '/mês'}.`);
      setPackMsg(msgs.join(' '));
      setExtraSeats(null);
      setVolumePacks(null);
      invalidate();
      await refresh({ force: true });
    } catch (e) {
      if (shouldFallbackToCheckout(e)) {
        onNavigate(
          `${PORTAL_PATHS.CHECKOUT}?interval=${interval === 'YEARLY' ? 'yearly' : 'monthly'}&seatCount=${sc}&volumePacks=${vp}`,
        );
        return;
      }
      setPackErr(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const saveReduction = async () => {
    setReduceErr(null);
    setReduceMsg(null);
    if (!reduceDirty || !reduceValid) {
      setReduceErr('Escolha menos assentos ou menos volume — ou cancele a assinatura por completo.');
      return;
    }
    if (redSc > currentSeatCount || redVp > currentVolumePacks) {
      setReduceErr('Para ampliar o plano, use a seção Ampliar plano acima.');
      return;
    }
    setBusy(true);
    try {
      const res = await updateSubscriptionPacks(redSc, redVp);
      setReduceMsg(
        res.asaasSyncPending
          ? `Redução agendada no X9 para a próxima renovação (${brl(res.nextChargeCents)}${interval === 'YEARLY' ? '/ano' : '/mês'}). Você mantém a capacidade atual até lá. O valor no Asaas ainda não mudou — a diferença será alinhada na renovação.`
          : `Redução agendada para a próxima renovação. Próxima cobrança: ${brl(res.nextChargeCents)}${interval === 'YEARLY' ? '/ano' : '/mês'}. Você mantém a capacidade atual até lá.`,
      );
      setReduceExtra(null);
      setReduceVolume(null);
      invalidate();
      await refresh({ force: true });
    } catch (e) {
      setReduceErr(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const midCyclePreview = useMemo(() => {
    if (interval !== 'YEARLY' || !preview || !dirty) return null;
    const currentMonthly = packPricing({
      seatCount: currentSeatCount,
      volumePacks: currentVolumePacks,
      interval: 'MONTHLY',
    }).monthlyCents;
    if (preview.monthlyCents <= currentMonthly) return null;
    const renewal = sub?.renewalDueDate || sub?.nextDueDate || null;
    return yearlyMidCycleUpgradeCharge({
      currentMonthlyCents: currentMonthly,
      targetMonthlyCents: preview.monthlyCents,
      openMonths: monthsUntilYmd(renewal),
    });
  }, [interval, preview, dirty, currentSeatCount, currentVolumePacks, sub]);

  const doCancel = async () => {
    setPackErr(null);
    setBusy(true);
    try {
      const res = await cancelMySubscription();
      setCancelMsg(
        res.accessUntil
          ? `Assinatura cancelada. Seu acesso continua até ${new Date(res.accessUntil).toLocaleDateString('pt-BR')}.`
          : 'Assinatura cancelada. Seu acesso continua até o fim do período já pago.',
      );
      setConfirmCancel(false);
      invalidate();
      await refresh({ force: true });
    } catch (e) {
      setPackErr(callableErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteUserAccount();
      await signOutBilling();
      onSignedOut();
    } catch (e) {
      const code =
        e && typeof e === 'object' && 'code' in e ? String((e as { code: string }).code) : '';
      if (
        code.includes('unauthenticated') ||
        code.includes('user-not-found') ||
        code.includes('user-token-expired')
      ) {
        await signOutBilling().catch(() => undefined);
        onSignedOut();
        return;
      }
      setDeleteError(callableErrorMessage(e));
      setDeleteStep('idle');
    } finally {
      setDeleting(false);
    }
  };

  const modalPayment = docModal
    ? (paymentOverrides[docModal.payment.id] || docModal.payment)
    : null;
  const modalFaturaReady = !!(modalPayment && faturaUrl(modalPayment));
  const modalNfReady = !!(modalPayment?.nfsePdfUrl);
  const modalCdcEligible = !!(modalPayment?.cdcRefundEligible);
  const modalAlreadyRefunded = String(modalPayment?.refundStatus || '').toUpperCase() === 'REFUNDED';

  return (
    <PortalShell
      active="account"
      onNavigate={onNavigate}
      title="Minha conta"
      description="Uso, plano e pagamentos vinculados ao app X9."
      backHref="/settings"
    >
      <PageTabs tabs={TABS} active={tab} onChange={changeTab} />

      {tab === 'uso' && (
        <div role="tabpanel">
          <UsageDashboard
            onNavigate={onNavigate}
            onUpgrade={() => changeTab('assinatura')}
            onCheckout={onCheckout}
          />
        </div>
      )}

      {tab === 'assinatura' && (
        <div className="space-y-5" role="tabpanel">
          {loading && !summary && <PortalLoader />}
          {error && (
            <div className="space-y-2">
              <p className="text-sm text-red-600">{error}</p>
              <button type="button" className="text-sm text-foreground" onClick={() => void refresh({ force: true })}>
                Tentar de novo
              </button>
            </div>
          )}

          {summary && (
            <>
              <Card className="border-2 border-border">
                <CardHeader>
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Plano atual
                  </p>
                  <CardTitle className="text-xl">{plan.title}</CardTitle>
                  <CardDescription>{plan.detail}</CardDescription>
                </CardHeader>
                {(summary.entitlement?.validUntil || plan.key !== 'FREE') && (
                  <CardContent className="space-y-2">
                    {summary.entitlement?.validUntil && (
                      <p className="text-xs text-muted-foreground">
                        Válido até{' '}
                        {new Date(summary.entitlement.validUntil).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                    {plan.key !== 'FREE' && (
                      <Button
                        variant="secondary"
                        width="auto"
                        size="sm"
                        onClick={() => onNavigate(PORTAL_PATHS.SEATS)}
                      >
                        Gerenciar equipe
                      </Button>
                    )}
                  </CardContent>
                )}
              </Card>

            {plan.key === 'FREE' && (
              <PlanPicker
                showFreeTier={false}
                onSelectMonthly={onSelectMonthly}
                onSelectYearly={onSelectYearly}
              />
            )}

              {plan.key === 'MONTHLY' && (
                <Card className="border-2 border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle>Upgrade para anual</CardTitle>
                      <Badge>−20%</Badge>
                    </div>
                    <CardDescription>
                      {brl(AMOUNTS_CENTAVOS.ANNUAL)}/ano — equivale a{' '}
                      {brl(AMOUNTS_CENTAVOS.ANNUAL / 12)}/mês. Substituirá seu plano mensal.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button width="auto" size="sm" onClick={onSelectYearly}>
                      Fazer upgrade para anual
                    </Button>
                  </CardContent>
                </Card>
              )}

              {plan.key !== 'FREE' && !cancelling && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Ampliar plano</CardTitle>
                    <CardDescription>
                      {interval === 'YEARLY'
                        ? 'Só aumentos — cobram a extensão até a renovação anual (−20%).'
                        : 'Só aumentos — entram na próxima fatura mensal.'}
                      {' '}Para reduzir, use a seção abaixo.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <PackStepper
                      label="Assentos extras"
                      hint={`Base ${BASE_TEAM_SEATS} inclusos (${brl(BASE_TEAM_PRICE_CENTAVOS)}/mês) · +${brl(EXTRA_SEAT_PRICE_CENTAVOS)}/mês cada · total ${sc} assentos`}
                      value={ex}
                      min={currentExtraSeats}
                      max={MAX_EXTRA_SEATS}
                      disabled={busy}
                      onChange={(n) => { setExtraSeats(Math.max(currentExtraSeats, n)); setPackMsg(null); }}
                    />
                    <PackStepper
                      label="Pacotes de volume"
                      hint={`+${INBOUND_PACK_VOLUME} contribuições/mês por pacote · ${brl(INBOUND_PACK_PRICE_CENTAVOS)}/mês`}
                      value={vp}
                      min={currentVolumePacks}
                      max={MAX_VOLUME_PACKS}
                      disabled={busy}
                      onChange={(n) => { setVolumePacks(Math.max(currentVolumePacks, n)); setPackMsg(null); }}
                    />
                    {dirty && targetValid && preview && (
                      <div className="space-y-1 text-sm text-foreground">
                        {midCyclePreview && midCyclePreview.chargeNowCents > 0 && (
                          <p>
                            Cobrança agora:{' '}
                            <span className="font-bold">{brl(midCyclePreview.chargeNowCents)}</span>
                            {' '}({midCyclePreview.openMonths} mês(es) até a renovação)
                          </p>
                        )}
                        <p>
                          {interval === 'YEARLY' ? 'Renovação anual' : 'Próxima fatura'}:{' '}
                          <span className="font-bold">{brl(preview.chargeCents)}</span>
                          {interval === 'YEARLY' ? '/ano' : '/mês'}
                          {' · '}{sc} assentos ·{' '}
                          {INBOUND_FREE_MONTHLY
                            + (sc > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
                            + vp * INBOUND_PACK_VOLUME}{' '}
                          contribuições/mês
                        </p>
                      </div>
                    )}
                    {interval === 'MONTHLY' && dirty && targetValid && preview && (
                      <div className="rounded-xl border border-border bg-muted p-3 space-y-2">
                        <p className="text-sm text-foreground">
                          Economize −20%: troque para anual com esta combinação. O tempo já pago
                          do mensal é somado ao período anual.
                        </p>
                        <Button
                          variant="secondary"
                          width="auto"
                          size="sm"
                          disabled={busy}
                          onClick={() => onNavigate(
                            `${PORTAL_PATHS.CHECKOUT}?interval=yearly&seatCount=${sc}&volumePacks=${vp}`,
                          )}
                        >
                          Trocar para anual ·{' '}
                          {brl(packPricing({
                            seatCount: sc,
                            volumePacks: vp,
                            interval: 'YEARLY',
                          }).chargeCents)}
                          /ano
                        </Button>
                      </div>
                    )}
                    {packMsg && <p className="text-sm text-emerald-700">{packMsg}</p>}
                    {packErr && <p className="text-sm text-red-600">{packErr}</p>}
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        width="auto"
                        size="sm"
                        disabled={busy || !dirty || !targetValid}
                        onClick={() => void savePacks()}
                      >
                        {busy
                          ? 'Salvando…'
                          : midCyclePreview && midCyclePreview.chargeNowCents > 0
                            ? `Ir ao pagamento · ${brl(midCyclePreview.chargeNowCents)}`
                            : 'Confirmar aumento'}
                      </Button>
                      {dirty && (
                        <Button
                          variant="ghost"
                          width="auto"
                          size="sm"
                          disabled={busy}
                          onClick={() => { setExtraSeats(null); setVolumePacks(null); setPackMsg(null); setPackErr(null); }}
                        >
                          Desfazer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {plan.key !== 'FREE' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Reduzir ou cancelar</CardTitle>
                    <CardDescription>
                      Prefira reduzir o plano e continuar com a X9. Reduções valem na próxima
                      renovação — você mantém o que já pagou neste ciclo.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {cancelling || cancelMsg ? (
                      <p className="text-sm text-muted-foreground">
                        {cancelMsg ||
                          'Cancelamento agendado: não haverá novas cobranças e seu acesso continua até o fim do período já pago.'}
                      </p>
                    ) : (
                      <>
                        {(sub?.pendingSeatCount != null || sub?.pendingSeatPacks != null || sub?.pendingVolumePacks != null) && (
                          <p className="text-xs text-muted-foreground">
                            Redução já agendada para a próxima renovação:
                            {sub?.pendingSeatCount != null && ` ${sub.pendingSeatCount} assentos`}
                            {sub?.pendingSeatCount == null && sub?.pendingSeatPacks != null
                              && ` ${sub.pendingSeatPacks * 5} assentos`}
                            {(sub?.pendingSeatCount != null || sub?.pendingSeatPacks != null)
                              && sub?.pendingVolumePacks != null && ' ·'}
                            {sub?.pendingVolumePacks != null && ` ${sub.pendingVolumePacks} pacote(s) de volume`}
                            .
                          </p>
                        )}

                        {currentSeatCount > 0 && (
                          <PackStepper
                            label="Assentos extras (após renovação)"
                            hint={`Hoje: ${currentSeatCount} assentos · mínimo do Equipe: ${BASE_TEAM_SEATS}`}
                            value={redEx}
                            min={0}
                            max={currentExtraSeats}
                            disabled={busy}
                            onChange={(n) => { setReduceExtra(Math.min(currentExtraSeats, n)); setReduceMsg(null); }}
                          />
                        )}
                        <PackStepper
                          label="Pacotes de volume (após renovação)"
                          hint={`Hoje: ${currentVolumePacks} pacote(s) · +${INBOUND_PACK_VOLUME}/mês cada`}
                          value={redVp}
                          min={0}
                          max={currentVolumePacks}
                          disabled={busy}
                          onChange={(n) => { setReduceVolume(Math.min(currentVolumePacks, n)); setReduceMsg(null); }}
                        />

                        {reduceDirty && reduceValid && reducePreview && (
                          <p className="text-sm text-foreground">
                            Após a renovação:{' '}
                            <span className="font-bold">{brl(reducePreview.chargeCents)}</span>
                            {interval === 'YEARLY' ? '/ano' : '/mês'}
                            {' · '}{redSc} assentos ·{' '}
                            {INBOUND_FREE_MONTHLY
                              + (redSc > 0 ? SEAT_PACK_INCLUDED_INBOUND : 0)
                              + redVp * INBOUND_PACK_VOLUME}{' '}
                            contribuições/mês
                          </p>
                        )}
                        {!reduceValid && reduceDirty && (
                          <p className="text-sm text-red-600">
                            Para zerar assentos e volume, cancele a assinatura por completo abaixo.
                          </p>
                        )}
                        {reduceMsg && <p className="text-sm text-emerald-700">{reduceMsg}</p>}
                        {reduceErr && <p className="text-sm text-red-600">{reduceErr}</p>}

                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="secondary"
                            width="auto"
                            size="sm"
                            disabled={busy || !reduceDirty || !reduceValid}
                            onClick={() => void saveReduction()}
                          >
                            {busy ? 'Salvando…' : 'Agendar redução na renovação'}
                          </Button>
                          {reduceDirty && (
                            <Button
                              variant="ghost"
                              width="auto"
                              size="sm"
                              disabled={busy}
                              onClick={() => {
                                setReduceExtra(null);
                                setReduceVolume(null);
                                setReduceMsg(null);
                                setReduceErr(null);
                              }}
                            >
                              Desfazer
                            </Button>
                          )}
                        </div>

                        <div className="border-t border-border pt-4 space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Cancelar por completo encerra as cobranças futuras. O acesso continua
                            até o fim do período já pago; depois a conta volta ao plano gratuito.
                          </p>
                          {confirmCancel ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-foreground">
                                Confirmar cancelamento total?
                              </span>
                              <Button
                                variant="secondary"
                                width="auto"
                                size="sm"
                                disabled={busy}
                                onClick={() => void doCancel()}
                              >
                                {busy ? 'Cancelando…' : 'Sim, cancelar tudo'}
                              </Button>
                              <Button
                                variant="ghost"
                                width="auto"
                                size="sm"
                                disabled={busy}
                                onClick={() => setConfirmCancel(false)}
                              >
                                Voltar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              width="auto"
                              size="sm"
                              disabled={busy}
                              onClick={() => setConfirmCancel(true)}
                            >
                              Cancelar assinatura por completo
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'pagamento' && (
        <div className="space-y-5" role="tabpanel">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Forma de pagamento</CardTitle>
              <CardDescription>
                Cartão tokenizado via Asaas.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {profile?.cardLast4 ? (
                <>
                  <div>
                    <p className="text-muted-foreground">Cartão</p>
                    <p className="font-medium text-foreground">
                      {(profile.cardBrand || 'Cartão').toString()} ·••• {profile.cardLast4}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Nenhum cartão salvo nesta tela ainda. O cartão informado no checkout fica na
                  Asaas (a X9 guarda no máximo bandeira e últimos 4 dígitos).
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'cobrancas' && (
        <div className="space-y-5" role="tabpanel">
          {scheduled && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Próxima cobrança</CardTitle>
                <CardDescription>
                  O que está contratado para a próxima fatura — não é um pagamento já feito.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {scheduled.cancelling ? (
                  <p className="text-foreground">
                    Nenhuma cobrança futura. O acesso segue até{' '}
                    {scheduled.dueLabel || 'o fim do período já pago'}.
                  </p>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-muted-foreground">
                        {scheduled.dueLabel
                          ? `Vencimento ${scheduled.dueLabel}`
                          : 'Data a confirmar na renovação'}
                      </p>
                      <p className="text-base font-semibold tabular-nums text-foreground">
                        {brl(scheduled.chargeCents)}
                        <span className="ml-1 text-sm font-medium text-muted-foreground">
                          /{scheduled.interval === 'YEARLY' ? 'ano' : 'mês'}
                        </span>
                      </p>
                    </div>
                    <p className="text-foreground">
                      X9 Equipe · {scheduled.interval === 'YEARLY' ? 'anual' : 'mensal'}
                      {scheduled.nextSeats > 0 ? ` · ${scheduled.nextSeats} assento${scheduled.nextSeats === 1 ? '' : 's'}` : ''}
                      {scheduled.nextVolume > 0
                        ? ` · ${scheduled.nextVolume} pacote${scheduled.nextVolume === 1 ? '' : 's'} de volume`
                        : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {inboundMonthly(scheduled.nextSeats, scheduled.nextVolume)} contribuições/mês
                      de outras pessoas neste plano.
                    </p>
                    {scheduled.structureChanged && (
                      <p className="text-xs text-muted-foreground">
                        Hoje você tem {scheduled.currentSeats} assentos
                        {scheduled.currentVolume > 0 ? ` e ${scheduled.currentVolume} pacote(s) de volume` : ''}
                        . A mudança vale nesta data.
                      </p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Histórico de cobranças</CardTitle>
              <CardDescription>
                O que foi cobrado, período coberto, descontos e documentos (Fatura e NF).
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-hidden">
              {loading && !summary ? (
                <PortalLoader />
              ) : payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum pagamento registrado ainda.</p>
              ) : (
                <>
                  {/* Compact until xl: sidebar + mid widths don't fit the full table */}
                  <ul className="divide-y divide-black/10 xl:hidden">
                    {payments.map((p) => {
                      const open = !!expandedCharges[p.id];
                      const chargeStatus = paymentChargeStatus(p);
                      return (
                        <li key={p.id} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-start gap-2">
                            <button
                              type="button"
                              className="min-w-0 flex-1 text-left"
                              aria-expanded={open}
                              onClick={() => toggleChargeExpanded(p.id)}
                            >
                              <div className="flex items-baseline justify-between gap-3">
                                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                  <span className="text-muted-foreground">{paymentDateLabel(p)}</span>
                                  <ChargeStatusBadge status={chargeStatus} />
                                </span>
                                <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                                  {brl(p.chargeCents ?? p.grossAmountInCents)}
                                </span>
                              </div>
                              <p className={cn('mt-1 text-sm text-foreground', !open && 'line-clamp-2')}>
                                {open ? whatLabel(p) : whatSummary(p)}
                              </p>
                            </button>
                            <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                width="auto"
                                size="icon"
                                className="h-9 w-9"
                                aria-label="Baixar documentos"
                                onClick={() => openDownloadModal(p)}
                              >
                                <IoDownloadOutline className="h-5 w-5" aria-hidden />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                width="auto"
                                size="icon"
                                className="h-9 w-9"
                                aria-label={open ? 'Recolher detalhes' : 'Expandir detalhes'}
                                aria-expanded={open}
                                onClick={() => toggleChargeExpanded(p.id)}
                              >
                                {open ? (
                                  <IoChevronUp className="h-5 w-5" aria-hidden />
                                ) : (
                                  <IoChevronDown className="h-5 w-5" aria-hidden />
                                )}
                              </Button>
                            </div>
                          </div>
                          {open && (
                            <div className="mt-2 space-y-1.5 border-l-2 border-border pl-3 text-sm text-muted-foreground">
                              <p className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-foreground">Status:</span>
                                <ChargeStatusBadge status={chargeStatus} />
                              </p>
                              <p>
                                <span className="font-medium text-foreground">Período:</span>{' '}
                                {formatPeriod(p.billingPeriodStart, p.billingPeriodEnd)}
                              </p>
                              <p>
                                <span className="font-medium text-foreground">Descontos:</span>{' '}
                                {discountLabel(p)}
                              </p>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {/* Wide desktop only: enough room beside the sidebar */}
                  <div className="hidden xl:block">
                    <table className="w-full table-fixed border-collapse text-left text-sm">
                      <colgroup>
                        <col className="w-[9%]" />
                        <col className="w-[34%]" />
                        <col className="w-[14%]" />
                        <col className="w-[14%]" />
                        <col className="w-[12%]" />
                        <col className="w-[9%]" />
                        <col className="w-[8%]" />
                      </colgroup>
                      <thead>
                        <tr className="border-b border-border">
                          <th className="py-2.5 pr-3 font-semibold text-muted-foreground">Data</th>
                          <th className="py-2.5 pr-3 font-semibold text-muted-foreground">O que</th>
                          <th className="py-2.5 pr-3 font-semibold text-muted-foreground">Período</th>
                          <th className="py-2.5 pr-3 font-semibold text-muted-foreground">Descontos</th>
                          <th className="py-2.5 pr-3 font-semibold text-muted-foreground">Status</th>
                          <th className="py-2.5 pr-3 text-right font-semibold text-muted-foreground">Valor</th>
                          <th className="py-2.5 text-center font-semibold text-muted-foreground">
                            <span className="sr-only">Documentos</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => {
                          const chargeStatus = paymentChargeStatus(p);
                          return (
                            <tr key={p.id} className="border-b border-border align-top">
                              <td className="py-3 pr-3 whitespace-nowrap text-foreground">
                                {paymentDateLabel(p)}
                              </td>
                              <td className="py-3 pr-3 text-foreground [overflow-wrap:break-word]">
                                {whatLabel(p)}
                              </td>
                              <td className="py-3 pr-3 text-muted-foreground [overflow-wrap:break-word]">
                                {formatPeriod(p.billingPeriodStart, p.billingPeriodEnd)}
                              </td>
                              <td className="py-3 pr-3 text-muted-foreground [overflow-wrap:break-word]">
                                {discountLabel(p)}
                              </td>
                              <td className="py-3 pr-3">
                                <ChargeStatusBadge status={chargeStatus} />
                              </td>
                              <td className="py-3 pr-3 whitespace-nowrap text-right font-semibold tabular-nums text-foreground">
                                {brl(p.chargeCents ?? p.grossAmountInCents)}
                              </td>
                              <td className="py-2 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  width="auto"
                                  size="icon"
                                  className="h-9 w-9"
                                  aria-label="Baixar documentos"
                                  onClick={() => openDownloadModal(p)}
                                >
                                  <IoDownloadOutline className="h-5 w-5" aria-hidden />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'detalhes' && (
        <div className="space-y-5" role="tabpanel">
          <Card>
            <CardHeader>
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                Identidade
              </p>
              <CardTitle className="text-base">Dados da conta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Telefone</p>
                <p className="font-medium text-foreground">{user?.phoneNumber || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">E-mail (NFS-e / recuperação)</p>
                {emailVerified && verifiedEmail ? (
                  <p className="font-medium text-foreground">{verifiedEmail}</p>
                ) : (
                  <button type="button" className="text-left font-medium text-foreground" onClick={onAddEmail}>
                    Adicionar e verificar e-mail
                  </button>
                )}
              </div>
              <div>
                <p className="text-muted-foreground">ID da conta</p>
                <p className="break-all font-mono text-xs text-foreground">{user?.uid || '—'}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-red-200">
            <CardHeader>
              <CardTitle className="text-base text-red-700">Excluir conta</CardTitle>
              <CardDescription>
                Remove permanentemente sua conta, tarefas e pastas que você criou. Não pode ser
                desfeito. Cancele a assinatura na aba Assinatura antes, se ainda estiver ativa.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {deleteStep === 'idle' && (
                <Button
                  variant="secondary"
                  width="auto"
                  size="sm"
                  className="border-red-300 text-red-700"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteStep('confirm');
                  }}
                >
                  Excluir conta
                </Button>
              )}

              {deleteStep === 'confirm' && (
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    Tem certeza? Você precisará se cadastrar de novo com o mesmo telefone para usar o X9.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="destructive"
                      width="auto"
                      size="sm"
                      onClick={() => setDeleteStep('final')}
                    >
                      Continuar
                    </Button>
                    <Button
                      variant="ghost"
                      width="auto"
                      size="sm"
                      onClick={() => setDeleteStep('idle')}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {deleteStep === 'final' && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-red-700">
                    Última confirmação: excluir permanentemente?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="destructive"
                      width="auto"
                      size="sm"
                      disabled={deleting}
                      onClick={() => void runDelete()}
                    >
                      {deleting ? 'Excluindo…' : 'Excluir permanentemente'}
                    </Button>
                    <Button
                      variant="ghost"
                      width="auto"
                      size="sm"
                      disabled={deleting}
                      onClick={() => setDeleteStep('idle')}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {docModal && modalPayment && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !docBusy && !cdcBusy) {
              setDocModal(null);
              setDocMsg(null);
              setCdcConfirm(false);
              setCdcErr(null);
            }
          }}
        >
          <DialogContent
            className="max-h-[90vh] max-w-md overflow-y-auto"
            aria-labelledby="doc-modal-title"
          >
            <DialogHeader>
              <DialogTitle id="doc-modal-title">
                {modalAlreadyRefunded || cdcOk ? 'Compra cancelada' : 'Baixar documentos'}
              </DialogTitle>
              <DialogDescription>
                {paymentDateLabel(modalPayment)} · {brl(modalPayment.chargeCents ?? modalPayment.grossAmountInCents)}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              {!(modalAlreadyRefunded || cdcOk) && (
                <>
                  <div className="rounded-xl border border-border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">Fatura</p>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          Comprovante / cobrança Asaas
                        </p>
                        <p className={cn(
                          'mt-1 text-xs font-semibold',
                          modalFaturaReady ? 'text-emerald-700' : 'text-amber-800',
                        )}
                        >
                          {modalFaturaReady ? 'Disponível' : 'Indisponível'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        width="auto"
                        size="sm"
                        disabled={docBusy}
                        onClick={() => void downloadFaturaFromModal()}
                      >
                        {modalFaturaReady ? 'Abrir' : (docBusy ? 'Atualizando…' : 'Atualizar')}
                      </Button>
                    </div>
                    {!modalFaturaReady && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Pode aparecer logo após o pagamento. Toque em Atualizar para buscar de novo.
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">NF</p>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          Nota fiscal de serviço (NFS-e)
                        </p>
                        <p className={cn(
                          'mt-1 text-xs font-semibold',
                          modalNfReady ? 'text-emerald-700' : 'text-amber-800',
                        )}
                        >
                          {modalNfReady ? 'Disponível' : 'Indisponível'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        width="auto"
                        size="sm"
                        variant={modalNfReady ? 'default' : 'secondary'}
                        disabled={docBusy}
                        onClick={() => downloadNfFromModal()}
                      >
                        {modalNfReady ? 'Abrir' : 'Ver status'}
                      </Button>
                    </div>
                    {!modalNfReady && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Emissão fiscal municipal em configuração. Quando emitida, o PDF fica disponível aqui.
                      </p>
                    )}
                  </div>
                </>
              )}

              {(modalCdcEligible || modalAlreadyRefunded || cdcOk) && (
                <div className={cn(
                  'rounded-xl border p-3',
                  modalAlreadyRefunded || cdcOk
                    ? 'border-emerald-200 bg-emerald-50/70'
                    : 'border-red-200 bg-red-50/40',
                )}
                >
                  <p className="font-medium text-foreground">
                    {modalAlreadyRefunded || cdcOk ? 'Estorno' : 'Cancelar esta compra'}
                  </p>
                  {modalAlreadyRefunded || cdcOk ? (
                    <p className="mt-1 text-sm text-emerald-800">
                      {cdcOk || 'Esta compra já foi estornada. O crédito no cartão pode levar até 10 dias úteis.'}
                    </p>
                  ) : !cdcConfirm ? (
                    <>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Você tem até 7 dias para desistir desta compra com estorno
                        {modalPayment.cdcRefundDaysLeft != null
                          ? ` (${modalPayment.cdcRefundDaysLeft} dia${modalPayment.cdcRefundDaysLeft === 1 ? '' : 's'} restante${modalPayment.cdcRefundDaysLeft === 1 ? '' : 's'})`
                          : ''}
                        . Renovações não entram neste direito.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        width="auto"
                        size="sm"
                        className="mt-3 border-red-300 text-red-700"
                        disabled={cdcBusy || docBusy}
                        onClick={() => {
                          setCdcErr(null);
                          setCdcConfirm(true);
                        }}
                      >
                        Cancelar e estornar
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="mt-1 text-sm text-foreground">{cdcConsequenceCopy(modalPayment)}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        O estorno no cartão pode levar até 10 dias úteis, conforme a operadora.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="destructive"
                          width="auto"
                          size="sm"
                          disabled={cdcBusy}
                          onClick={() => void confirmCdcRefund()}
                        >
                          {cdcBusy ? 'Cancelando…' : 'Confirmar cancelamento'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          width="auto"
                          size="sm"
                          disabled={cdcBusy}
                          onClick={() => {
                            setCdcConfirm(false);
                            setCdcErr(null);
                          }}
                        >
                          Voltar
                        </Button>
                      </div>
                    </>
                  )}
                  {cdcErr && <p className="mt-2 text-sm text-red-600">{cdcErr}</p>}
                </div>
              )}
            </div>

            {docMsg && <p className="mt-3 text-sm text-amber-800">{docMsg}</p>}
          </DialogContent>
        </Dialog>
      )}
    </PortalShell>
  );
}
